import { AbstractControl, ValidationErrors } from "@angular/forms";
import { Moment } from "./moment";

const EMAIL_TEST =
  /^\s*[\w\-\+_]+(\.[\w\-\+_]+)*\@[\w\-\+_]+\.[\w\-\+_]+(\.[\w\-\+_]+)*\s*$/;
const CELULAR_TEST = /^[0-9]{2}[6789]{1}[0-9]{8}$/;

const CPF_INVALID = { appCPF: true };
const CNPJ_INVALID = { appCNPJ: true };


const isEmptyValue = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  return false;
};

export class FormValidators {

  static email(control: AbstractControl): ValidationErrors {
    if (isEmptyValue(control.value)) {
      return null;
    }

    return EMAIL_TEST.test(control.value) ? null : { appEmail: true };
  }

  static link(control: AbstractControl): ValidationErrors {
    if (isEmptyValue(control.value)) {
      return null;
    }

    return control.value.length > 5 ? null : { appLink: true };
  }

  static minValue(value: number) {
    return (control: AbstractControl): ValidationErrors => {
      if (isEmptyValue(control.value)) {
        return null;
      }
      return +control.value >= +value ? null : { appMinValue: true };
    };
  }

  static maxValue(value: number) {
    return (control: AbstractControl): ValidationErrors => {
      if (isEmptyValue(control.value)) {
        return null;
      }
      return +control.value > +value ? { appMaxValue: true } : null;
    };
  }

  static datetime(format?: string) {
    return (control: AbstractControl): ValidationErrors => {
      if (isEmptyValue(control.value)) {
        return null;
      }
      return !Moment(control.value, format).isValid()
        ? { appDatetime: true }
        : null;
    };
  }

  static minItems(min: number = 1) {
    return (control: AbstractControl): ValidationErrors => {
      const items = Array.isArray(control.value) ? control.value : [];
      const total = items.filter((item) => !item || !item.deleted).length;
      return total >= min ? null : { appMinItems: { required: min, actual: total } };
    };
  }

  static rangeValue(min: number, max: number) {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value < min) {
        return { appMinValue: true };
      }

      if (control.value > max) {
        return { appMaxValue: true };
      }

      return null;
    };
  }

  static doValidateCelPhone(value: string): boolean {
    const numero = value.trim().replace(/[^0-9]/g, "");
    return CELULAR_TEST.test(numero);
  }

  static celular(control: AbstractControl): ValidationErrors {
    if (isEmptyValue(control.value)) {
      return null;
    }

    return this.doValidateCelPhone(control.value) ? null : { celular: true };
  }

  static cpf(control: AbstractControl): ValidationErrors | null {
    const cpf = (control.value || "").trim().replace(/[^0-9]/g, "");

    if (isEmptyValue(control.value)) {
      return null;
    }

    if (
      cpf.length !== 11 ||
      cpf === "00000000000" ||
      cpf === "11111111111" ||
      cpf === "22222222222" ||
      cpf === "33333333333" ||
      cpf === "44444444444" ||
      cpf === "55555555555" ||
      cpf === "66666666666" ||
      cpf === "77777777777" ||
      cpf === "88888888888" ||
      cpf === "99999999999"
    ) {
      return CPF_INVALID;
    }

    // Valida primeiro digito
    const soma_d1 = cpf
      .split("")
      .splice(0, 9)
      .map((val, index) => +val * (10 - index))
      .reduce((sum, num) => sum + num, 0);

    let rev_d1 = 11 - (soma_d1 % 11);

    if (rev_d1 === 10 || rev_d1 === 11) {
      rev_d1 = 0;
    }

    if (rev_d1 !== +cpf.charAt(9)) {
      return CPF_INVALID;
    }

    // Valida segundo digito
    const soma_d2 = cpf
      .split("")
      .splice(0, 10)
      .map((val, index) => +val * (11 - index))
      .reduce((sum, num) => sum + num, 0);

    let rev_d2 = 11 - (soma_d2 % 11);
    if (rev_d2 === 10 || rev_d2 === 11) {
      rev_d2 = 0;
    }

    if (rev_d2 !== +cpf.charAt(10)) {
      return CPF_INVALID;
    }

    return null;
  }

  static cnpj(control: AbstractControl): ValidationErrors | null {
    const cnpj = (control.value || "")
      .trim()
      .replace(/[.\-\/\s]/g, "")
      .toUpperCase();

    if (isEmptyValue(cnpj)) {
      return null;
    }

    // Deve ter 14 caracteres: 12 alfanuméricos + 2 dígitos verificadores
    if (cnpj.length !== 14 || !/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) {
      return CNPJ_INVALID;
    }

    // Rejeita CNPJs com todos os caracteres iguais
    if (new Set(cnpj).size === 1) {
      return CNPJ_INVALID;
    }

    // Conversão ASCII-48 (0-9 → 0-9, A-Z → 17-42)
    const valores = cnpj
      .split("")
      .map((c) => c.charCodeAt(0) - 48);

    // 1o dígito verificador
    const pesos_d1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma_d1 = pesos_d1.reduce(
      (sum, peso, i) => sum + valores[i] * peso,
      0
    );
    const resto_d1 = soma_d1 % 11;
    const dv1 = resto_d1 < 2 ? 0 : 11 - resto_d1;

    if (dv1 !== valores[12]) {
      return CNPJ_INVALID;
    }

    // 2o dígito verificador
    const pesos_d2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma_d2 = pesos_d2.reduce(
      (sum, peso, i) => sum + valores[i] * peso,
      0
    );
    const resto_d2 = soma_d2 % 11;
    const dv2 = resto_d2 < 2 ? 0 : 11 - resto_d2;

    if (dv2 !== valores[13]) {
      return CNPJ_INVALID;
    }

    return null;
  }
}
